import en from '../i18n/en.json'
import ko from '../i18n/ko.json'

type Language = 'en' | 'ko'
type TranslationKey = string

const translations = { en, ko }

class I18n {
  // Resolved lazily on first access so the module is safe to import in a
  // non-browser (Node/tsx) context — e.g. the SEO/sitemap generator — where
  // `localStorage`/`navigator` do not exist. Defaults to 'en' off-browser.
  private currentLanguage: Language | null = null
  private listeners: Set<() => void> = new Set()

  private detectLanguage(): Language {
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('language')
        if (saved === 'en' || saved === 'ko') return saved
      }
    } catch { /* URL/browser language still works when storage is blocked. */ }
    if (typeof navigator !== 'undefined' && navigator.language.startsWith('ko')) {
      return 'ko'
    }
    return 'en'
  }

  getCurrentLanguage(): Language {
    if (this.currentLanguage === null) {
      this.currentLanguage = this.detectLanguage()
    }
    return this.currentLanguage
  }

  setLanguage(lang: Language) {
    this.currentLanguage = lang
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('language', lang)
    } catch { /* The current language does not require persistent storage. */ }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang
    }
    this.notifyListeners()
  }

  t(key: TranslationKey, language: Language = this.getCurrentLanguage()): string {
    const keys = key.split('.')
    let value: any = translations[language]

    for (const k of keys) {
      value = value?.[k]
    }

    return value || key
  }

  onChange(callback: () => void) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private notifyListeners() {
    this.listeners.forEach(callback => callback())
  }
}

export const i18n = new I18n()

export function initI18n() {
  document.documentElement.lang = i18n.getCurrentLanguage()
}

export function t(key: TranslationKey, language?: Language): string {
  return i18n.t(key, language)
}

export function getCurrentLanguage(): Language {
  return i18n.getCurrentLanguage()
}

/**
 * Bilingual text type for data files
 */
export interface BilingualText {
  en: string
  ko: string
}

/**
 * Bilingual array type for data files
 */
export interface BilingualArray {
  en: string[]
  ko: string[]
}

/**
 * Get text from bilingual data based on current language
 */
export function localizedText(text: BilingualText): string {
  return text[i18n.getCurrentLanguage()]
}

/**
 * Get array from bilingual data based on current language
 */
export function localizedArray(arr: BilingualArray): string[] {
  return arr[i18n.getCurrentLanguage()]
}
